import React, { useState } from 'react';
import { openProvider } from '../../utils/providerLinking';
import { AnalyticsService } from '../../services/analyticsService';
import styles from './ProviderSection.module.css';

const ProviderSection = ({ providers, primaryProvider, tmdbId, kind, seasonNumber, title, availabilityStatus }) => {
    const [showAll, setShowAll] = useState(false);

    const inTheaters = availabilityStatus?.inTheaters ?? false;
    const estimatedStreamingDate = availabilityStatus?.estimatedStreamingDate ?? null;
    const releaseDate = availabilityStatus?.releaseDate ?? null;
    const status = availabilityStatus?.status ?? null;

    const formatShortDate = (iso) => {
        if (!iso) return null;
        try {
            return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        } catch { return null; }
    };

    if (!providers || providers.length === 0) {
        if (availabilityStatus && (inTheaters || status === 'upcoming')) {
            return (
                <div className={styles.container}>
                    {/*<h3 className={styles.title}>Where to Watch</h3>*/}
                    <div className={styles.theaterBox}>
                        <span className={styles.theaterIcon}>🎟</span>
                        <div>
                            <p className={styles.theaterTitle}>
                                {inTheaters ? 'In Theaters Now' : releaseDate ? `Coming ${formatShortDate(releaseDate)}` : 'Coming Soon'}
                            </p>
                            {inTheaters && estimatedStreamingDate && (
                                <p className={styles.theaterSub}>
                                    Estimated streaming release: {formatShortDate(estimatedStreamingDate)}
                                </p>
                            )}
                            {!inTheaters && releaseDate && (
                                <p className={styles.theaterSub}>Not yet available to stream or rent</p>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    // Deduplicate and prioritize providers
    const uniqueProviders = [];
    const seenNames = new Set();

    const streamProviders = providers.filter(p => p.type === 'stream');
    const rentProviders = providers.filter(p => p.type === 'rent');
    const buyProviders = providers.filter(p => p.type === 'buy');

    const allByPriority = [...streamProviders, ...rentProviders, ...buyProviders];

    for (const provider of allByPriority) {
        const key = provider.name.toLowerCase();
        if (!seenNames.has(key)) {
            uniqueProviders.push(provider);
            seenNames.add(key);
        }
    }

    const displayedProviders = showAll ? uniqueProviders : uniqueProviders.slice(0, 6);

    const handleProviderClick = async (provider) => {
        // Track analytics
        try {
            await AnalyticsService.trackProviderClick({
                tmdbId,
                kind,
                seasonNumber,
                providerName: provider.name,
                providerType: provider.type,
                surface: 'web',
            });
        } catch (error) {
            console.error('Failed to track provider click:', error);
            // Don't block the user from opening the provider
        }

        // Open the provider
        openProvider(provider, title);
    };

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>Where to Watch</h3>

            {/* In-theaters banner when movie is also still in theaters */}
            {inTheaters && (
                <div className={styles.theaterBox}>
                    <span className={styles.theaterIcon}>🎟</span>
                    <div>
                        <p className={styles.theaterTitle}>Also In Theaters</p>
                        {estimatedStreamingDate && (
                            <p className={styles.theaterSub}>
                                Streaming estimated around {formatShortDate(estimatedStreamingDate)}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {primaryProvider && (
                <div className={styles.primaryProvider}>
                    <button
                        onClick={() => handleProviderClick(primaryProvider)}
                        className={styles.watchNowButton}
                    >
                        {primaryProvider.logoUrl && (
                            <img
                                src={primaryProvider.logoUrl}
                                alt={primaryProvider.name}
                                className={styles.providerLogo}
                            />
                        )}
                        <span>Watch on {primaryProvider.name}</span>
                    </button>
                </div>
            )}

            <div className={styles.providerGrid}>
                {displayedProviders.map((provider, index) => (
                    <button
                        key={`${provider.name}-${index}`}
                        className={styles.providerCard}
                        onClick={() => handleProviderClick(provider)}
                    >
                        {provider.logoUrl && (
                            <img
                                src={provider.logoUrl}
                                alt={provider.name}
                                className={styles.providerImage}
                            />
                        )}
                        <div className={styles.providerInfo}>
                            <p className={styles.providerName}>{provider.name}</p>
                            <span className={styles.providerType}>
                                {provider.type === 'stream' ? '📺 Stream' :
                                    provider.type === 'rent' ? '💰 Rent' : '🛒 Buy'}
                            </span>
                        </div>
                    </button>
                ))}
            </div>

            {uniqueProviders.length > 6 && (
                <button
                    className={styles.seeAllButton}
                    onClick={() => setShowAll(!showAll)}
                >
                    {showAll ? 'Show less' : `See all ${uniqueProviders.length} options`}
                </button>
            )}
        </div>
    );
};

export default ProviderSection;